import React, { FormEvent, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { fetchApi } from "../../common/api";
import { supabase } from "../../common/supabase";
import { getMemoriesPath } from "../../Router/routes";
import { setUserAction } from "../../state/user/actions";
import { User } from "../../state/user/types";
import logo from "../../assets/logo512.png";
import styles from "./styles.stylesheet.module.scss";

type LocationState = {
  from?: {
    pathname?: string;
  };
};

export const SignIn = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [authError, setAuthError] = useState("");

  const isDisabled = useMemo(
    () => isLoading || !email.trim() || !password,
    [isLoading, email, password],
  );

  const handleSignIn = async (event: FormEvent) => {
    event.preventDefault();
    if (isDisabled) return;

    setIsLoading(true);
    setAuthError("");

    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });

    if (error || !data.user || !data.session) {
      setAuthError("Email or password is incorrect.");
      setIsLoading(false);
      return;
    }

    await supabase.auth.setSession(data.session);

    const userProfile = await fetchApi<User>("/users/get_by_auth_id", "POST", {
      auth_id: data.user.id,
    });
    await setUserAction(userProfile)(dispatch);
    navigate(state?.from?.pathname || getMemoriesPath(), { replace: true });
    setIsLoading(false);
  };

  return (
    <main className={styles.authPage}>
      <section className={styles.authShellCentered}>
        <img src={logo} alt="Replay" className={styles.authLogo} />
        <div className={styles.authCopyCentered}>
          <h1>Welcome Back</h1>
        </div>
        <form className={styles.authCard} onSubmit={handleSignIn}>
          <label className={styles.authField}>
            <span>Email</span>
            <input
              type="email"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                setAuthError("");
              }}
              placeholder="you@example.com"
              autoComplete="email"
              disabled={isLoading}
            />
          </label>
          <label className={styles.authField}>
            <span>Password</span>
            <input
              type="password"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
                setAuthError("");
              }}
              placeholder="Enter your password"
              autoComplete="current-password"
              disabled={isLoading}
            />
          </label>
          {authError && (
            <p className={styles.authError} role="alert">
              {authError}
            </p>
          )}
          <button
            className={styles.authPrimary}
            type="submit"
            disabled={isDisabled}
          >
            {isLoading ? "Signing in..." : "Sign in"}
          </button>
        </form>
      </section>
    </main>
  );
};
